import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { Scale, RefreshCw, Save, Lock, CheckCircle2, AlertTriangle, Settings as SettingsIcon, X } from 'lucide-react';
import toast from 'react-hot-toast';
import CageBalancingTabs from '../components/CageBalancingTabs';
import CageDatePicker from '../components/CageDatePicker';
import { useCageDate, CAGE_OFFSET } from '../contexts/CageDateContext';
import { currentBusinessDate } from '../utils/casinoDay';
import { useAuth } from '../contexts/AuthContext';

// ----------------------------------------------------------------
// Cage Chip Liability reconciliation
// ----------------------------------------------------------------
// Live Liability = live chips held (auto from CBS, override allowed) − LIVE float.
// FP Liability   = FP chips held (from Free Play) − FP float.
// Total          = Live + FP.  Overage = (6668 + 1417 bank balances) + Total.
// Cashier/manager enters (cbs:liability:enter); exec signs w/ PIN (cbs:liability:sign).

const fmtCents = (c) =>
  c == null ? '—' : (Number(c) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const dollarsToCents = (v) => Math.round(Number(v || 0) * 100);
// Presentational row — module-level so it is NOT recreated on every keystroke.
// (Defining it inside the component remounted this subtree on each input, which
// caused the page to jump/scroll while typing.)
function Row({ label, value, tone, sub, strong }) {
  return (
    <div className={`flex items-center justify-between ${strong ? 'border-t border-gray-700 pt-2 mt-1.5' : ''}`}>
      <span className={strong ? 'text-xs uppercase tracking-wide text-gray-400' : 'text-sm text-gray-400'}>
        {label}
        {sub ? <span className="ml-2 text-xs text-gray-600">{sub}</span> : null}
      </span>
      <span
        className={`font-mono ${strong ? 'text-lg font-bold' : 'text-sm'} ${
          tone === 'good' ? 'text-emerald-400' : tone === 'bad' ? 'text-red-400' : 'text-gray-200'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

export default function CageChipLiability() {
  const { can } = useAuth();
  const canEnter = can('cbs:liability:enter');
  const canSign = can('cbs:liability:sign');
  const canConfig = can('cbs:configure');

  const { businessDate: date, setBusinessDate: setDate } = useCageDate(CAGE_OFFSET.CLOSED_DAY);
  const [data, setData] = useState(null);
  const [overrideStr, setOverrideStr] = useState('');
  const [bankStr, setBankStr] = useState('');
  const [bank1417Str, setBank1417Str] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pin, setPin] = useState(null); // sign modal
  const [unlockOpen, setUnlockOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/cbs/liability', { params: { date } });
      const d = res.data;
      setData(d);
      // Pre-fill with whatever the day already stands at: the entered count if there
      // is one, otherwise the CBS close. Leaving it untouched re-saves the same
      // number, so agreeing with the CBS costs no keystrokes.
      const seed = d.liveHeldOverride && d.liveHeldCents != null ? d.liveHeldCents : d.liveHeldFromCbsCents;
      setOverrideStr(seed != null ? String(seed / 100) : '');
      setBankStr(d.bankAccountBalanceCents != null ? String(d.bankAccountBalanceCents / 100) : '');
      setBank1417Str(d.bankAccount1417Cents != null ? String(d.bankAccount1417Cents / 100) : '');
      setNotes(d.notes || '');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load liability.');
    } finally {
      setLoading(false);
    }
  }, [date]);
  useEffect(() => {
    load();
  }, [load]);

  if (loading || !data) {
    return (
      <div>
        <CageBalancingTabs />
        <div className="p-8 text-center text-gray-400">Loading…</div>
      </div>
    );
  }

  const signed = data.signed;
  const liveHeldCents = overrideStr !== '' ? dollarsToCents(overrideStr) : (data.liveHeldFromCbsCents ?? null);
  // Only meaningful once there IS a CBS close to compare the count against.
  const countVarianceCents =
    data.liveHeldFromCbsCents != null && liveHeldCents != null
      ? liveHeldCents - data.liveHeldFromCbsCents
      : null;
  const liveLiabilityCents = liveHeldCents == null ? null : liveHeldCents - data.liveFloatCents;
  const fpLiabilityCents = data.fpLiabilityCents;
  const totalLiabilityCents = liveLiabilityCents == null ? null : liveLiabilityCents + fpLiabilityCents;
  const bank6668Cents = bankStr !== '' ? dollarsToCents(bankStr) : (data.bankAccountBalanceCents ?? null);
  const bank1417Cents = bank1417Str !== '' ? dollarsToCents(bank1417Str) : (data.bankAccount1417Cents ?? null);
  // Overage is measured against BOTH accounts, matching the Chip Liability Log.
  const bankCents =
    bank6668Cents == null && bank1417Cents == null ? null : (bank6668Cents || 0) + (bank1417Cents || 0);
  const overageCents = bankCents != null && totalLiabilityCents != null ? bankCents + totalLiabilityCents : null;

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/admin/cbs/liability', {
        businessDate: date,
        // Send it as an override only when it genuinely differs, so a day where the
        // count agrees with the CBS stays flagged as CBS-derived rather than being
        // recorded as a manual figure that happens to match.
        liveHeldOverrideCents:
          overrideStr !== '' && dollarsToCents(overrideStr) !== data.liveHeldFromCbsCents
            ? dollarsToCents(overrideStr)
            : null,
        bankAccountBalanceCents: bankStr !== '' ? dollarsToCents(bankStr) : null,
        bankAccount1417Cents: bank1417Str !== '' ? dollarsToCents(bank1417Str) : null,
        notes: notes || null,
      });
      toast.success('Liability saved.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to save liability.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <CageBalancingTabs />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
            <Scale className="text-amber-400" /> Chip Liability
          </h1>
          <p className="text-sm text-gray-400">
            Live + Free Play chip liability vs the bank balance. Live held is the morning
            count, pre-filled from the CBS close; FP pulls from the Free Play count.
          </p>
          {data && data.countedOnDate ? (
            <p className="mt-1 text-xs text-gray-500">
              This is {date}&rsquo;s closing position &mdash; the count taken on the morning of{' '}
              <span className="text-gray-400">{data.countedOnDate}</span>. In the Chip
              Liability log that is the row dated{' '}
              <span className="font-mono text-amber-300">{data.countedOnDate}</span>, not {date}.
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <CageDatePicker offsetDays={CAGE_OFFSET.CLOSED_DAY} showEntryDate />
          <button onClick={load} className="flex items-center gap-2 rounded-lg border border-gray-600 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800">
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      {signed && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-700/50 bg-emerald-900/20 p-3 text-sm text-emerald-300">
          <span className="flex items-center gap-2"><Lock size={16} /> Signed &amp; locked for {date}. Overage {fmtCents(data.overageCents)}.</span>
          {canSign && (
            <button onClick={() => setUnlockOpen(true)} className="flex items-center gap-2 rounded-lg border border-emerald-600/60 px-3 py-1 text-xs font-medium text-emerald-200 hover:bg-emerald-800/30">
            <RefreshCw size={13} /> Unlock (supervisor)
          </button>
        )}
        </div>
      )}
      {unlockOpen && (
        <UnlockModal date={date} onClose={() => setUnlockOpen(false)} onDone={async () => { setUnlockOpen(false); await load(); }} />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Reconciliation */}
        <div className="rounded-xl border border-gray-700 bg-gray-800 p-5 space-y-4">
          {/* Live */}
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Live chips</div>
            {/* The morning count is a real, independent count - not an exception to the
                CBS close. So it is a first-class field, pre-filled with the CBS figure
                (which already matches about three days in four) and showing the
                variance when it does not. Nobody has to decide whether they are
                allowed to disagree with the system. */}
            <Row
              label="CBS close"
              sub={data.hasCbs ? `${data.cbsShift || ''} shift` : 'no CBS close yet'}
              value={fmtCents(data.liveHeldFromCbsCents)}
            />
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">
                Live held
                <span className="ml-2 text-xs text-gray-600">counted</span>
              </span>
              {canEnter && !signed ? (
                <input
                  type="number"
                  step="0.01"
                  placeholder="$"
                  value={overrideStr}
                  onChange={(e) => setOverrideStr(e.target.value)}
                  className="no-spin w-32 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-right text-white font-mono text-sm focus:outline-none focus:border-amber-500"
                />
              ) : (
                <span className="font-mono text-gray-300">{fmtCents(liveHeldCents)}</span>
              )}
            </div>
            {countVarianceCents != null && countVarianceCents !== 0 && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Count vs CBS close</span>
                <span className={`font-mono ${countVarianceCents > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {countVarianceCents > 0 ? '+' : ''}{fmtCents(countVarianceCents)}
                </span>
              </div>
            )}
            <Row label="Live float" value={fmtCents(data.liveFloatCents)} />
            <Row label="Live liability" value={fmtCents(liveLiabilityCents)} tone={liveLiabilityCents > 0 ? 'good' : 'bad'} />
            {!data.hasCbs && overrideStr === '' && (
              <div className="flex items-center gap-1 text-[11px] text-amber-400">
                <AlertTriangle size={12} /> No CBS close for {date} yet — enter that day&rsquo;s CBS, or type the counted Live held.
              </div>
            )}
          </div>
          {/* FP */}
          <div className="space-y-1 border-t border-gray-700 pt-3">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Free play</div>
            <Row
              label="FP held"
              sub={data.hasFreePlayCount ? 'from Free Play' : 'no count yet'}
              value={fmtCents(data.fpHeldCents)}
            />
            <Row label="FP float" value={fmtCents(data.fpFloatCents)} />
            <Row label="FP liability" value={fmtCents(fpLiabilityCents)} tone={fpLiabilityCents > 0 ? 'good' : 'bad'} />
            {!data.hasFreePlayCount && (
              <div className="flex items-center gap-1 text-[11px] text-amber-400">
                <AlertTriangle size={12} /> No Free Play count for {date} — enter it on the Free Play tab.
              </div>
            )}
          </div>
          {/* Total + bank + overage */}
          <div className="space-y-1 border-t border-gray-700 pt-3">
            <Row label="Total liability" value={fmtCents(totalLiabilityCents)} strong />
            <div className="flex items-center justify-between text-sm pt-2">
              <span className="text-gray-400">6668 bank balance</span>
              {canEnter && !signed ? (
                <input
                  type="number"
                  step="0.01"
                  placeholder="$"
                  value={bankStr}
                  onChange={(e) => setBankStr(e.target.value)}
                  className="no-spin w-32 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-right text-white font-mono text-sm focus:outline-none focus:border-amber-500"
                />
              ) : (
                <span className="font-mono text-gray-300">{fmtCents(bank6668Cents)}</span>
              )}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">1417 bank balance</span>
              {canEnter && !signed ? (
                <input
                  type="number"
                  step="0.01"
                  placeholder="$"
                  value={bank1417Str}
                  onChange={(e) => setBank1417Str(e.target.value)}
                  className="no-spin w-32 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-right text-white font-mono text-sm focus:outline-none focus:border-amber-500"
                />
              ) : (
                <span className="font-mono text-gray-300">{fmtCents(bank1417Cents)}</span>
              )}
            </div>
            <Row label="Bank total (6668 + 1417)" value={fmtCents(bankCents)} />
            <Row
              label="Overage"
              value={fmtCents(overageCents)}
              tone={overageCents == null ? undefined : overageCents >= 0 ? 'good' : 'bad'}
              strong
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Notes (transfers, checks, etc.)</label>
            <input
              type="text"
              disabled={!canEnter || signed}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-2 py-1.5 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-amber-500 disabled:opacity-50"
            />
          </div>
          {!signed && (
            <div className="flex items-center gap-2">
              {canEnter && (
                <button onClick={save} disabled={saving} className="flex items-center gap-2 rounded-lg bg-gray-700 hover:bg-gray-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40">
                  <Save size={16} /> {saving ? 'Saving…' : 'Save'}
                </button>
              )}
              {canSign && (
                <button
                  onClick={() => setPin({ bank: bankStr, bank1417: bank1417Str })}
                  disabled={bankStr === ''}
                  title={bankStr === '' ? 'Enter the 6668 bank balance first' : 'Sign & lock (supervisor PIN)'}
                  className="flex items-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-600 px-4 py-2 text-sm font-medium text-black disabled:opacity-40"
                >
                  <Lock size={16} /> Sign &amp; lock
                </button>
              )}
            </div>
          )}
        </div>

        {/* Float config */}
        {canConfig && <ChipFloats />}
      </div>

      {pin && (
        <SignModal
          date={date}
          bankStr={bankStr}
          bank1417Str={bank1417Str}
          notes={notes}
          onClose={() => setPin(null)}
          onDone={async () => {
            setPin(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function ChipFloats() {
  const [floats, setFloats] = useState([]);
  const [f, setF] = useState({ effectiveDate: currentBusinessDate(), liveFloat: '', fpFloat: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/cbs/chip-floats');
      setFloats(res.data.floats || []);
    } catch {
      /* perm */
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!f.effectiveDate || f.liveFloat === '' || f.fpFloat === '') return toast.error('Enter date + both floats.');
    setSaving(true);
    try {
      await api.post('/admin/cbs/chip-floats', {
        effectiveDate: f.effectiveDate,
        liveFloatCents: dollarsToCents(f.liveFloat),
        fpFloatCents: dollarsToCents(f.fpFloat),
      });
      toast.success('Float saved.');
      setF({ effectiveDate: currentBusinessDate(), liveFloat: '', fpFloat: '' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to save float.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-700 bg-gray-800 p-5">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-white">
        <SettingsIcon size={18} className="text-amber-400" /> Chip Floats
      </h2>
      <p className="mb-3 text-xs text-gray-500">
        The live + FP float constants the liability is measured against. Effective-dated — a new row
        applies from its date forward; historical days use the float in force then.
      </p>
      <div className="grid grid-cols-3 gap-2">
        <input type="date" value={f.effectiveDate} onChange={(e) => setF((p) => ({ ...p, effectiveDate: e.target.value }))} className="rounded border border-gray-700 bg-gray-950 px-2 py-1.5 text-sm text-gray-100" />
        <input type="number" step="0.01" placeholder="Live float $" value={f.liveFloat} onChange={(e) => setF((p) => ({ ...p, liveFloat: e.target.value }))} className="no-spin rounded border border-gray-700 bg-gray-950 px-2 py-1.5 text-sm text-gray-100" />
        <input type="number" step="0.01" placeholder="FP float $" value={f.fpFloat} onChange={(e) => setF((p) => ({ ...p, fpFloat: e.target.value }))} className="no-spin rounded border border-gray-700 bg-gray-950 px-2 py-1.5 text-sm text-gray-100" />
      </div>
      <button onClick={add} disabled={saving} className="mt-2 rounded-lg bg-amber-500 px-3 py-2 text-sm font-medium text-black disabled:opacity-40">
        {saving ? 'Saving…' : 'Add / update float'}
      </button>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="py-2 pr-4 font-medium">Effective</th>
              <th className="py-2 pr-4 font-medium text-right">Live float</th>
              <th className="py-2 pr-4 font-medium text-right">FP float</th>
            </tr>
          </thead>
          <tbody>
            {floats.map((r) => (
              <tr key={r.id} className="border-t border-gray-800 text-gray-300">
                <td className="py-1.5 pr-4 font-mono">{String(r.effectiveDate).slice(0, 10)}</td>
                <td className="py-1.5 pr-4 text-right font-mono">{fmtCents(r.liveFloatCents)}</td>
                <td className="py-1.5 pr-4 text-right font-mono">{fmtCents(r.fpFloatCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UnlockModal({ date, onClose, onDone }) {
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [staffPin, setStaffPin] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!employeeNumber || !staffPin) return;
    setBusy(true);
    try {
      await api.post('/admin/cbs/liability/unlock', { businessDate: date, employeeNumber, staffPin });
      toast.success(`Unlocked ${date} — Free Play and liability are editable again.`);
      await onDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Unlock failed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-xl border border-gray-700 bg-gray-800 p-5">
        <div className="mb-1 flex items-start justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-white">
            <RefreshCw size={18} className="text-amber-400" /> Unlock chip liability
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <p className="mb-4 text-sm text-gray-400">Enter a supervisor cage PIN to reopen {date}. This also unlocks that day's Free Play count.</p>
        <label className="block text-xs text-gray-400 mb-1">Employee number</label>
        <input autoFocus value={employeeNumber} onChange={(e) => setEmployeeNumber(e.target.value)} className="mb-3 w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:outline-none focus:border-amber-500" />
        <label className="block text-xs text-gray-400 mb-1">Cage PIN</label>
        <input type="password" value={staffPin} onChange={(e) => setStaffPin(e.target.value)} className="mb-4 w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:outline-none focus:border-amber-500" />
        <button type="submit" disabled={busy || !employeeNumber || !staffPin} className="w-full rounded-lg bg-amber-500 px-3 py-2 font-medium text-black hover:bg-amber-600 disabled:opacity-50">
          {busy ? 'Unlocking…' : 'Unlock'}
        </button>
      </form>
    </div>
  );
}

function SignModal({ date, bankStr, bank1417Str = '', notes, onClose, onDone }) {
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [staffPin, setStaffPin] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!employeeNumber || !staffPin) return;
    setBusy(true);
    try {
      const res = await api.post('/admin/cbs/liability/sign', {
        businessDate: date,
        bankAccountBalanceCents: dollarsToCents(bankStr),
        bankAccount1417Cents: bank1417Str !== '' ? dollarsToCents(bank1417Str) : null,
        notes: notes || null,
        employeeNumber,
        staffPin,
      });
      const ov = res.data?.overageCents;
      toast.success(`Signed — overage ${fmtCents(ov)}.`);
      await onDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Sign failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-xl border border-gray-700 bg-gray-800 p-5">
        <div className="mb-1 flex items-start justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-white">
            <CheckCircle2 size={18} className="text-amber-400" /> Sign chip liability
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <p className="mb-4 text-sm text-gray-400">Enter a supervisor cage PIN to lock {date}.</p>
        <label className="block text-xs text-gray-400 mb-1">Employee number</label>
        <input autoFocus value={employeeNumber} onChange={(e) => setEmployeeNumber(e.target.value)} className="mb-3 w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:outline-none focus:border-amber-500" />
        <label className="block text-xs text-gray-400 mb-1">Cage PIN</label>
        <input type="password" value={staffPin} onChange={(e) => setStaffPin(e.target.value)} className="mb-4 w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:outline-none focus:border-amber-500" />
        <button type="submit" disabled={busy || !employeeNumber || !staffPin} className="w-full rounded-lg bg-amber-500 px-3 py-2 font-medium text-black hover:bg-amber-600 disabled:opacity-50">
          {busy ? 'Signing…' : 'Sign & lock'}
        </button>
      </form>
    </div>
  );
}
